import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Stack,
  Paper,
  Typography,
  Divider,
  CircularProgress,
} from '@mui/material';
import { toast } from '../../../../../ui/toast';
import { Button, Dialog } from '../../../../../ui';
import {
  IUserDisplay,
  PermissionType,
  createAccessControl,
  deleteAccessControl,
  fetchAccessControlList,
} from '../../../../../utils/api_service/access_control';
import { getEmployeeList } from '../../../../../utils/api_service/employees';
import { EmployeeUser } from '../../../../../utils/types/admin_types';
import { SelectOptions } from '../../../../../utils/types/react_select_types';
import { DIALOG_TYPE } from '../../../Constants/DialogTypes';
import { SelectedUserPermissions } from '../JobRequisition/Components/SelectedUserPermissions';
import { ContributorSelectWithDisplay } from './Components/ContributorSelectWithDisplay';

// Utility function for debouncing
function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number,
): T & { cancel: () => void } {
  let timeout: NodeJS.Timeout | null = null;

  const debounced = ((...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  }) as T & { cancel: () => void };

  debounced.cancel = () => {
    if (timeout) {
      clearTimeout(timeout);
      timeout = null;
    }
  };

  return debounced;
}

// Utility functions
function sanitizePermissions(perms: string[] | undefined): PermissionType[] {
  if (!perms) return [];
  return perms.filter(
    (p): p is PermissionType => p === 'read' || p === 'write' || p === 'delete',
  );
}

function mapACLToUserDisplay(aclEntry: any): IUserDisplay {
  return {
    firstName: aclEntry.orgUser.user.firstName,
    lastName: aclEntry.orgUser.user.lastName,
    email: aclEntry.orgUser.user.email,
    id: aclEntry.orgUser.user.id,
    orgUserId: aclEntry.orgUser.id,
    permissions: sanitizePermissions(aclEntry.permissions),
  };
}

function mapEmployeeToUserDisplay(
  employee: EmployeeUser,
  permissions: PermissionType[] = [],
): IUserDisplay {
  return {
    firstName: employee.user.firstName,
    lastName: employee.user.lastName,
    email: employee.user.email,
    id: employee.user.id,
    orgUserId: employee.id,
    permissions,
  };
}

export interface IPermissionsProps {
  resourceId: string;
  resourceType: string;
  defaultHiringManager: IUserDisplay[];
  defaultInternalRecruiter: IUserDisplay[];
  employees?: EmployeeUser[]; // Made optional since we'll fetch from API
}

export const Permissions: React.FC<IPermissionsProps> = ({
  resourceId,
  resourceType,
  defaultHiringManager = [],
  defaultInternalRecruiter = [],
  employees = [],
}) => {
  // Main state
  const [usersWithAccess, setUsersWithAccess] = useState<IUserDisplay[]>([]);
  const [assignableHMs, setAssignableHMs] = useState<IUserDisplay[]>([]);
  const [assignableIRs, setAssignableIRs] = useState<IUserDisplay[]>([]);
  const [allPermissions, setAllPermissions] = useState<
    Record<string, PermissionType[]>
  >({});

  // Dialog state
  const [confirmDialogState, setConfirmDialogState] = useState(false);
  const [userToRemove, setUserToRemove] = useState<IUserDisplay | null>(null);

  // Loading states
  const [loading, setLoading] = useState(false);
  const [isLoadingEmployees, setIsLoadingEmployees] = useState(false);

  // Employee/Contributor state
  const [searchTerm, setSearchTerm] = useState('');
  const [employeeList, setEmployeeList] = useState<EmployeeUser[]>(employees);
  const [selectedContributorOptions, setSelectedContributorOptions] = useState<
    SelectOptions[]
  >([]);
  const [selectedContributors, setSelectedContributors] = useState<
    IUserDisplay[]
  >([]);
  const [hasShownError, setHasShownError] = useState(false);

  // Memoized values
  const usersWithAccessIds = useMemo(
    () => new Set(usersWithAccess.map((u) => u.id)),
    [usersWithAccess],
  );

  const availableContributors = useMemo(
    () => employeeList.filter((emp) => !usersWithAccessIds.has(emp.user.id)),
    [employeeList, usersWithAccessIds],
  );

  const contributorOptions = useMemo(
    () =>
      availableContributors.map((employee) => ({
        value: employee.id,
        label: `${employee.user.firstName} ${employee.user.lastName}`,
      })),
    [availableContributors],
  );

  // Employee fetching with debounce
  const fetchEmployees = useCallback(
    async (search: string = '') => {
      setIsLoadingEmployees(true);
      setHasShownError(false);

      try {
        let allEmployees: EmployeeUser[] = [];
        let currentPage = 1;
        let hasMore = true;

        // Fetch all pages to get complete employee list
        while (hasMore) {
          const response = await getEmployeeList(search, currentPage, 50);

          if (!response.ok) {
            const errorData = await response.json();
            if (!hasShownError) {
              toast.error(errorData.error || 'Failed to fetch employees', {
                position: 'bottom-center',
              });
              setHasShownError(true);
            }
            throw new Error(errorData.error || 'Failed to fetch employees');
          }

          const data = await response.json();
          allEmployees = [...allEmployees, ...data.employee];
          hasMore = currentPage < data.pages;
          currentPage++;
        }

        setEmployeeList(allEmployees);
      } catch (error) {
        if (!hasShownError) {
          toast.error('Failed to fetch employees', {
            position: 'bottom-center',
          });
          setHasShownError(true);
        }
        setEmployeeList([]);
      } finally {
        setIsLoadingEmployees(false);
      }
    },
    [hasShownError],
  );

  const debouncedFetchEmployees = useMemo(
    () => debounce(fetchEmployees, 300),
    [fetchEmployees],
  );

  // Effects
  useEffect(() => {
    // Initial employee fetch
    fetchEmployees('');

    return () => {
      debouncedFetchEmployees.cancel();
    };
  }, [fetchEmployees, debouncedFetchEmployees]);

  useEffect(() => {
    // Debounced search when search term changes
    if (searchTerm !== '') {
      debouncedFetchEmployees(searchTerm);
    } else {
      fetchEmployees('');
    }
  }, [searchTerm, debouncedFetchEmployees, fetchEmployees]);

  // Access control list management
  const refetchAccessControlList = useCallback(() => {
    if (!resourceId || !resourceType) return;

    setLoading(true);
    fetchAccessControlList(resourceId, resourceType)
      .then((aclList) => {
        const aclUserDisplays = aclList.map(mapACLToUserDisplay);
        setUsersWithAccess(aclUserDisplays);

        const aclUserIds = new Set(aclUserDisplays.map((u) => u.id));
        setAssignableHMs(
          defaultHiringManager.filter((u) => !aclUserIds.has(u.id)),
        );
        setAssignableIRs(
          defaultInternalRecruiter.filter((u) => !aclUserIds.has(u.id)),
        );

        const perms: Record<string, PermissionType[]> = {};
        aclUserDisplays.forEach((u) => {
          perms[u.id] = u.permissions;
        });
        setAllPermissions(perms);
      })
      .catch((error) => {
        console.error('Error fetching access control list:', error);

      })
      .finally(() => setLoading(false));
  }, [
    resourceId,
    resourceType,
    defaultHiringManager,
    defaultInternalRecruiter,
  ]);

  useEffect(() => {
    refetchAccessControlList();
  }, [refetchAccessControlList]);

  // Permission management
  const handlePermissionChange = useCallback(
    (userId: string, perms: PermissionType[]) => {
      setAllPermissions((prev) => ({ ...prev, [userId]: perms }));
    },
    [],
  );

  // Contributor management
  const mapOptionToUserDisplay = useCallback(
    (option: SelectOptions): IUserDisplay | undefined => {
      const employee = employeeList.find((emp) => emp.id === option.value);
      if (!employee) return undefined;

      return mapEmployeeToUserDisplay(
        employee,
        allPermissions[employee.user.id] || [],
      );
    },
    [employeeList, allPermissions],
  );

  const handleContributorsChange = useCallback(
    (options: SelectOptions[]) => {
      // Filter out contributors who may have gained access since selection
      const filteredOptions = options.filter((opt) => {
        const employee = employeeList.find((emp) => emp.id === opt.value);
        return employee && !usersWithAccessIds.has(employee.user.id);
      });

      setSelectedContributorOptions(filteredOptions);

      const users = filteredOptions
        .map(mapOptionToUserDisplay)
        .filter((user): user is IUserDisplay => user !== undefined);

      setSelectedContributors(users);

      // Initialize permissions for new contributors
      users.forEach((user) => {
        setAllPermissions((prev) => ({
          ...prev,
          [user.id]: prev[user.id] || [],
        }));
      });
    },
    [employeeList, usersWithAccessIds, mapOptionToUserDisplay],
  );

  // Clean up selected contributors when they gain access
  useEffect(() => {
    if (selectedContributorOptions.length > 0) {
      const stillAvailable = selectedContributorOptions.filter((opt) => {
        const employee = employeeList.find((emp) => emp.id === opt.value);
        return employee && !usersWithAccessIds.has(employee.user.id);
      });

      if (stillAvailable.length !== selectedContributorOptions.length) {
        setSelectedContributorOptions(stillAvailable);
        const users = stillAvailable
          .map(mapOptionToUserDisplay)
          .filter((user): user is IUserDisplay => user !== undefined);
        setSelectedContributors(users);
      }
    }
  }, [
    usersWithAccessIds,
    employeeList,
    selectedContributorOptions,
    mapOptionToUserDisplay,
  ]);

  // Delete operations
  const handleDelete = useCallback(
    async (orgUserId: string) => {
      try {
        await deleteAccessControl({
          orgUserId,
          resourceId,
          resourceType,
        });
        toast.success('Access removed!', { position: 'bottom-center' });
        refetchAccessControlList();
      } catch (error) {
        toast.error('Failed to remove access.', { position: 'bottom-center' });
      }
    },
    [resourceId, resourceType, refetchAccessControlList],
  );

  const handleRequestDelete = useCallback((user: IUserDisplay) => {
    setUserToRemove(user);
    setConfirmDialogState(true);
  }, []);

  const handleConfirmRemove = useCallback(async () => {
    if (userToRemove?.orgUserId) {
      await handleDelete(userToRemove.orgUserId);
      setUserToRemove(null);
      setConfirmDialogState(false);
    }
  }, [userToRemove, handleDelete]);

  const handleCancelRemove = useCallback(() => {
    setUserToRemove(null);
    setConfirmDialogState(false);
  }, []);

  // Save permissions
  const handleSavePermissions = useCallback(async () => {
    const entries = [
      ...assignableHMs,
      ...assignableIRs,
      ...selectedContributors,
    ]
      .filter(
        (u) =>
          u.orgUserId && allPermissions[u.id] && allPermissions[u.id].length > 0,
      )
      .map((u) => ({
        orgUserId: u.orgUserId as string,
        permissions: allPermissions[u.id].map(String),
      }));

    if (entries.length === 0) {
      toast.info('No permissions selected.', { position: 'bottom-center' });
      return;
    }

    const payload = { resourceId, resourceType, entries };

    try {
      await createAccessControl(payload);
      toast.success('Permissions saved!', { position: 'bottom-center' });
      refetchAccessControlList();

      // Clear selected contributors after successful save
      setSelectedContributorOptions([]);
      setSelectedContributors([]);
    } catch (error) {
      toast.error('Failed to save permissions.', { position: 'bottom-center' });
    }
  }, [
    assignableHMs,
    assignableIRs,
    selectedContributors,
    allPermissions,
    resourceId,
    resourceType,
    refetchAccessControlList,
  ]);

  // Filter users by permission level
  const creatorsWithAccess = useMemo(
    () => usersWithAccess.filter((user) => user.permissions.includes('delete')),
    [usersWithAccess],
  );

  const otherUsersWithAccess = useMemo(
    () =>
      usersWithAccess.filter((user) => !user.permissions.includes('delete')),
    [usersWithAccess],
  );

  if (loading) {
    return (
      <Box sx={{ p: { xs: 1.5, sm: 3 }, width: '100%' }}>
        <Typography variant='h6' sx={{ mb: 2, fontWeight: 700 }}>
          Permissions
        </Typography>
        <Stack justifyContent='center' alignItems='center' sx={{ height: 128 }}>
          <CircularProgress size={32} sx={{ mb: 1 }} />
          <Typography variant='body2' color='text.secondary'>
            Loading permissions...
          </Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', minWidth: 0, p: { xs: 1, sm: 2, lg: 3 } }}>
      <Typography variant='h6' sx={{ mb: 2, fontWeight: 700, color: 'text.primary' }}>
        Permissions
      </Typography>

      <Stack direction={{ xs: 'column', xl: 'row' }} spacing={{ xs: 2, xl: 3 }}>
        { /* Left Column: Permissions List */ }
        <Box sx={{ minWidth: 0, flex: 1 }}>
          { /* Creator Access */ }
          { creatorsWithAccess.length > 0 && (
            <Paper variant='outlined' sx={{ p: { xs: 1.5, sm: 2 }, mb: 2, borderRadius: 2 }}>
              <SelectedUserPermissions
                title='Users with Creator Access'
                users={creatorsWithAccess}
                onPermissionChange={handlePermissionChange}
                readOnly
              />
            </Paper>
          ) }

          { /* Other Users with Access */ }
          { otherUsersWithAccess.length > 0 && (
            <Paper variant='outlined' sx={{ p: { xs: 1.5, sm: 2 }, mb: 2, borderRadius: 2 }}>
              <SelectedUserPermissions
                title='Users with Access'
                users={otherUsersWithAccess}
                onPermissionChange={handlePermissionChange}
                readOnly
                onDelete={handleRequestDelete}
              />
            </Paper>
          ) }

          { /* Assignable Hiring Managers */ }
          { assignableHMs.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Divider sx={{ borderStyle: 'dashed', my: 2 }} />
              <Paper variant='outlined' sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2 }}>
                <SelectedUserPermissions
                  title='Hiring Manager(s) – Assign Permissions'
                  users={assignableHMs}
                  onPermissionChange={handlePermissionChange}
                />
              </Paper>
            </Box>
          ) }

          { /* Assignable Internal Recruiters */ }
          { assignableIRs.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Divider sx={{ borderStyle: 'dashed', my: 2 }} />
              <Paper variant='outlined' sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2 }}>
                <SelectedUserPermissions
                  title='Internal Recruiter(s) – Assign Permissions'
                  users={assignableIRs}
                  onPermissionChange={handlePermissionChange}
                />
              </Paper>
            </Box>
          ) }

          { /* Selected Contributors */ }
          { selectedContributors.length > 0 && (
            <Box>
              <Divider sx={{ borderStyle: 'dashed', my: 2 }} />
              <Paper variant='outlined' sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2 }}>
                <SelectedUserPermissions
                  title='Contributors'
                  users={selectedContributors}
                  onPermissionChange={handlePermissionChange}
                />
              </Paper>
            </Box>
          ) }
        </Box>

        { /* Right Column: Contributor Select */ }
        <Box sx={{ width: { xs: '100%', xl: 300 }, flexShrink: { xl: 0 } }}>
          <Paper
            variant='outlined'
            sx={{
              p: { xs: 1.5, sm: 2 },
              borderRadius: 2,
              position: { xl: 'sticky' },
              top: { xl: 96 },
            }}
          >
            <Typography variant='subtitle1' sx={{ fontWeight: 600, mb: 1.5, color: 'text.primary' }}>
              Add Contributors
            </Typography>

            <ContributorSelectWithDisplay
              employees={availableContributors}
              editable={true}
              value={selectedContributorOptions}
              onChange={handleContributorsChange}
            />

            <Typography variant='caption' sx={{ mt: 1.5, display: 'block', color: 'text.secondary' }}>
              Contributors added here will appear in the panel on the left.
              Users already with access will not be shown.
            </Typography>
          </Paper>
        </Box>
      </Stack>

      { /* Action Buttons */ }
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent='flex-end'
        alignItems={{ sm: 'center' }}
        spacing={1}
        sx={{ mt: 2 }}
      >
        <Button
          label='Cancel'
          onClick={() => {
            setSelectedContributorOptions([]);
            setSelectedContributors([]);
          }}
          variant='secondary'
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        />
        <Button
          label='Save Permissions'
          onClick={handleSavePermissions}
          variant='primary'
          disabled={loading}
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        />
      </Stack>

      { /* Confirmation Dialog */ }
      <Dialog
        title='Remove Permission'
        handleApply={handleConfirmRemove}
        confirmButtonLable='Yes, Remove'
        isOpen={confirmDialogState}
        onClose={handleCancelRemove}
        cancelButtonLable='No, Cancel'
        dialogType={DIALOG_TYPE.WARNING}
        justify='end'
      >
        <Typography variant='body2' sx={{ color: 'text.secondary' }}>
          { userToRemove && (
            <>
              Are you sure you want to remove <br />
              <b>
                { userToRemove.firstName } { userToRemove.lastName } (
                { userToRemove.email })
              </b>{ ' ' }
              <br />
              from this resource?
              <br />
              <br />
              <Box component='ul' sx={{ pl: 2.5, m: 0 }}>
                <li>This action cannot be undone.</li>
                <li>Any permissions granted to this user will be lost.</li>
              </Box>
            </>
          ) }
        </Typography>
      </Dialog>
    </Box>
  );
};
